import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Mail,
  Plus,
  Minus,
  Briefcase,
  Handshake,
  Sparkles,
  Newspaper,
  Calendar,
  Target,
  Rocket,
  Zap,
  Palette,
  Wrench,
  Layers,
  Code2,
  Shuffle,
  Wallet,
  Search,
  Video,
  MousePointerClick,
  PenTool,
  Film,
} from "lucide-react";
import {
  Nav,
  Footer,
  Eyebrow,
  PillarAccent,
} from "../App";
import { useContactPanel } from "../components/ContactPanel";
import { useContent } from "../lib/useContent";
import { useSeo } from "../lib/useSeo";

/* ============================================================
   /contact — Sunday Digital
   Hybrid CRO contact page. All sections are CMS-editable.
============================================================ */

// Icon map for the Audience Routing cards (mirrors ContentEditPage
// SERVICE_ICON_OPTIONS — plus Sparkles / Handshake / Newspaper / Calendar
// which we reserve for contact-specific semantics).
const ROUTE_ICON_MAP = {
  Sparkles, Briefcase, Handshake, Newspaper, Calendar,
  Target, Rocket, Zap, Palette, Wrench, Layers, Code2,
  Shuffle, Wallet, Search, Video, MousePointerClick, PenTool, Film,
  Mail,
};

/* ============================================================
   01 · Hero
============================================================ */
const Hero = ({ onPrimary }) => {
  const cms = useContent("contact");
  const h = (cms && cms.hero) || {};
  const breadcrumb = h.breadcrumb || "Contact";
  const eyebrow = h.eyebrow || "Currently booking · Q3 2026";
  const titleLine1 = h.title_line_1 || "Let's";
  const titleLine2 = h.title_line_2 || "talk";
  const subtitle = (h.subtitle || "").trim() ||
    "Pick the path that fits — a 30-minute intro call, a written brief, or a direct line to the right team. Whichever you choose, we'll reply within 24 hours on weekdays.";
  const ctaLabel = h.cta_label || "Send a brief";
  return (
    <section
      id="top"
      data-testid="contact-hero"
      className="relative overflow-hidden bg-[#0B0F14]"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 600px at 75% 25%, rgba(244,214,78,0.10), transparent 60%)",
        }}
      />
      <span
        aria-hidden
        className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.07) 1px, transparent 0)",
          backgroundSize: "3px 3px",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-24 md:pb-32">
        <div
          data-reveal="eyebrow"
          className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-12 md:mb-16"
        >
          <Link to="/" className="hover:text-[#F4D64E] transition-colors">Sunday</Link>
          <span className="w-1 h-1 rounded-full bg-neutral-700" />
          <span className="text-[#F4D64E]">{breadcrumb}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-14 items-end">
          <div className="lg:col-span-8">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
            <h1
              data-testid="contact-hero-title"
              className="font-display mt-7 text-white leading-[0.9] tracking-[-0.045em] font-black text-[3.5rem] sm:text-[5rem] md:text-[7.5rem] lg:text-[10rem] xl:text-[12rem]"
            >
              <span className="block">{titleLine1}</span>
              <span className="block italic font-normal text-neutral-300">
                {titleLine2}<span className="text-[#F4D64E]">.</span>
              </span>
            </h1>
          </div>

          <div className="lg:col-span-4">
            <p className="text-[1.05rem] md:text-[1.18rem] text-neutral-300 leading-[1.6] max-w-[42ch]">
              {subtitle}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={onPrimary}
                data-testid="contact-hero-brief"
                className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-6 py-3.5 rounded-md transition-colors"
              >
                {ctaLabel}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   02 · Audience routing — editable cards
============================================================ */
const ROUTING_FALLBACK_ITEMS = [
  { tag_label: "New project", title: "Start a project",
    body: "Brief us on what you're building. We'll come back with a scope, a team, and a starting line.",
    cta_label: "Send a brief", href: "", is_primary: true, icon: "Sparkles" },
  { tag_label: "Careers", title: "Join the studio",
    body: "Designers, engineers, strategists, producers. Tell us what you do — and what you want to build next.",
    cta_label: "Apply now", href: "mailto:careers@sundaydigital.io", is_primary: false, icon: "Briefcase" },
  { tag_label: "Partnerships", title: "Build with us",
    body: "Tooling, platforms, agency collaborations. If your work touches ours, we want to hear from you.",
    cta_label: "Get in touch", href: "mailto:partnerships@sundaydigital.io", is_primary: false, icon: "Handshake" },
];

const RouteCard = ({ route, idx, onPrimary, isWide }) => {
  const Icon = ROUTE_ICON_MAP[route.icon] || Sparkles;
  const inner = (
    <div
      className={`relative h-full rounded-2xl border p-7 md:p-9 transition-all duration-500 ${
        route.is_primary
          ? "bg-gradient-to-br from-[#1a1505] to-[#0B0F14] border-[#F4D64E]/30 hover:border-[#F4D64E]/60"
          : "bg-[#0F141A] border-white/[0.06] hover:border-[#F4D64E]/30"
      }`}
    >
      <div className="flex items-start justify-between mb-7">
        <span
          className={`inline-flex items-center justify-center w-12 h-12 rounded-md ${
            route.is_primary
              ? "bg-[#F4D64E] text-neutral-950"
              : "bg-white/[0.05] text-[#F4D64E]"
          }`}
        >
          <Icon className="w-5 h-5" />
        </span>
        <span className="text-[0.6rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
          {route.tag_label}
        </span>
      </div>
      <h3 className="font-display text-[1.4rem] md:text-[1.65rem] font-black tracking-[-0.02em] leading-[1.15] text-white mb-3">
        {route.title}
      </h3>
      <p className="text-[0.95rem] text-neutral-400 leading-[1.6] mb-7 max-w-[36ch]">
        {route.body}
      </p>
      <span className="inline-flex items-center gap-2 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] group-hover:text-white transition-colors">
        {route.cta_label}
        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </div>
  );
  const wrapperClass = `group ${isWide ? "lg:col-span-6" : "lg:col-span-3"}`;
  const testId = `route-${(route.tag_label || route.title || `card-${idx+1}`).toLowerCase().replace(/\s+/g, "-")}`;

  // Primary card or card with empty href → open the in-site Contact panel.
  if (route.is_primary || !route.href) {
    return (
      <li data-testid={testId} className={wrapperClass}>
        <button type="button" onClick={onPrimary} className="block w-full h-full text-left">
          {inner}
        </button>
      </li>
    );
  }
  return (
    <li data-testid={testId} className={wrapperClass}>
      <a href={route.href} className="block h-full">{inner}</a>
    </li>
  );
};

const Routing = ({ onPrimary }) => {
  const cms = useContent("contact");
  const r = cms?.routing || {};
  const eyebrow = r.eyebrow || "Pick a path";
  const headlineMain = r.headline_main || "Choose your";
  const headlineItalic = r.headline_italic || "doorway";
  const headlineAccent = r.headline_accent || "in";
  const description = r.description || "";
  const items = Array.isArray(r.items) && r.items.length ? r.items : ROUTING_FALLBACK_ITEMS;
  const primaryIdx = items.findIndex((it) => it.is_primary);

  return (
    <section
      data-testid="contact-routing"
      className="relative bg-[#0B0F14] border-t border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-3xl mb-14 md:mb-20">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2.25rem] md:text-[3rem] lg:text-[3.75rem] font-black tracking-[-0.04em] leading-[0.98] text-white"
          >
            <span>
              {headlineMain}{" "}
              {headlineItalic ? (
                <>
                  <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                </>
              ) : null}
              <PillarAccent>{headlineAccent}</PillarAccent>.
            </span>
          </h2>
          {description ? (
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-8 text-[1rem] md:text-[1.1rem] text-neutral-400 leading-[1.65] max-w-[60ch]"
            >
              <span>{description}</span>
            </p>
          ) : null}
        </div>
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6">
          {items.map((route, i) => (
            <RouteCard
              key={i}
              idx={i}
              route={route}
              onPrimary={onPrimary}
              isWide={i === primaryIdx}
            />
          ))}
        </ul>
      </div>
    </section>
  );
};

/* ============================================================
   03 · Book a call — hidden by default, toggle-visible
============================================================ */
const BookACall = ({ onPrimary }) => {
  const cms = useContent("contact");
  const b = cms?.book_a_call || {};
  const eyebrow = b.eyebrow || "Direct booking";
  const headlineMain = b.headline_main || "30 minutes,";
  const headlineItalic = b.headline_italic || "no";
  const headlineAccent = b.headline_accent || "pitch deck";
  const body = b.body ||
    "A working call. Bring the goal, the constraint, the deadline. We'll bring the questions and an honest read on whether we're a fit.";
  const bullets = Array.isArray(b.bullets) && b.bullets.length
    ? b.bullets.map((it) => it.title || "").filter(Boolean)
    : ["Free, no commitment", "Real founders / leads on the call", "You leave with a written summary"];
  const embedUrl = b.embed_url || "https://cal.com/sundaydigital/intro";
  const fallbackCaption = b.fallback_caption || "Trouble with the embed?";
  const fallbackCtaLabel = b.fallback_cta_label || "Send a brief instead ↗";

  return (
    <section
      id="book"
      data-testid="contact-book"
      className="relative bg-[#0F141A] border-y border-white/[0.06] scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-12">
          <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
            <h2
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="mt-7 font-display text-[2rem] md:text-[2.5rem] lg:text-[3rem] font-black tracking-[-0.035em] leading-[1.05] text-white"
            >
              <span>
                {headlineMain}{" "}
                {headlineItalic ? (
                  <>
                    <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                  </>
                ) : null}
                <PillarAccent>{headlineAccent}</PillarAccent>.
              </span>
            </h2>
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-7 text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.65] max-w-[42ch]"
            >
              <span>{body}</span>
            </p>
            <ul className="mt-9 space-y-3 text-[0.92rem] text-neutral-400">
              {bullets.map((item, i) => (
                <li key={i} className="flex items-baseline gap-3">
                  <span className="text-[#F4D64E] mt-0.5">—</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-8">
            <div className="relative bg-[#0B0F14] rounded-2xl border border-white/[0.08] overflow-hidden">
              <iframe
                src={embedUrl}
                title="Book a call with Sunday Digital"
                data-testid="cal-embed"
                className="w-full h-[640px] md:h-[720px] block"
                loading="lazy"
                style={{ border: "none", colorScheme: "normal" }}
              />
            </div>
            <p className="mt-5 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-600 text-center">
              {fallbackCaption}{" "}
              <button
                type="button"
                onClick={onPrimary}
                className="text-[#F4D64E] hover:text-white transition-colors underline-offset-4 hover:underline"
                data-testid="cal-fallback"
              >
                {fallbackCtaLabel}
              </button>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   04 · FAQ — accordion
============================================================ */
const FAQ_FALLBACK_ITEMS = [
  { title: "What does a typical engagement cost?",
    body: "Most projects fall between $40k and $250k+ depending on scope. Retainers start at $12k/month for ongoing partnerships." },
  { title: "How long until we ship?",
    body: "A standard Shopify Plus build takes 10–14 weeks from kickoff to launch." },
];

const FAQItem = ({ item, idx, isOpen, onToggle }) => (
  <li
    className="border-b border-white/[0.08] last:border-b-0"
    data-testid={`faq-${idx + 1}`}
  >
    <button
      type="button"
      onClick={() => onToggle(idx)}
      className="w-full text-left py-7 md:py-9 flex items-baseline gap-6 group"
      aria-expanded={isOpen}
    >
      <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase tabular-nums text-[#F4D64E] w-12 shrink-0">
        {String(idx + 1).padStart(2, "0")}
      </span>
      <span className="flex-1 font-display text-[1.15rem] md:text-[1.4rem] lg:text-[1.55rem] font-bold tracking-[-0.015em] leading-[1.35] text-white group-hover:text-[#F4D64E] transition-colors">
        {item.title}
      </span>
      <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/[0.12] text-white group-hover:bg-[#F4D64E] group-hover:text-neutral-950 group-hover:border-[#F4D64E] transition-all">
        {isOpen ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
      </span>
    </button>
    <div
      className={`grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
      }`}
    >
      <div className="overflow-hidden">
        <div className="pb-8 md:pb-10 pl-[4.5rem] pr-12 max-w-[60ch]">
          <p className="text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.7]">
            {item.body}
          </p>
        </div>
      </div>
    </div>
  </li>
);

const FAQ = () => {
  const cms = useContent("contact");
  const f = cms?.faq || {};
  const eyebrow = f.eyebrow || "Frequently asked";
  const headlineMain = f.headline_main || "Before the";
  const headlineItalic = f.headline_italic || "first";
  const headlineAccent = f.headline_accent || "call";
  const description = f.description ||
    "The questions every client asks before they book. Read first; ask second.";
  const items = Array.isArray(f.items) && f.items.length ? f.items : FAQ_FALLBACK_ITEMS;
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section
      id="faq"
      data-testid="contact-faq"
      className="relative bg-[#0B0F14] border-t border-white/[0.06] scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-12">
          <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
            <h2
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="mt-7 font-display text-[2rem] md:text-[2.5rem] lg:text-[3rem] font-black tracking-[-0.035em] leading-[1.05] text-white"
            >
              <span>
                {headlineMain}{" "}
                {headlineItalic ? (
                  <>
                    <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                  </>
                ) : null}
                <PillarAccent>{headlineAccent}</PillarAccent>.
              </span>
            </h2>
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-7 text-[1rem] text-neutral-400 leading-[1.65] max-w-[40ch]"
            >
              <span>{description}</span>
            </p>
          </div>

          <ul className="lg:col-span-8 border-t border-white/[0.08]">
            {items.map((item, i) => (
              <FAQItem
                key={i}
                item={item}
                idx={i}
                isOpen={openIdx === i}
                onToggle={(n) => setOpenIdx(openIdx === n ? -1 : n)}
              />
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   05 · Direct details — email, socials, offices
============================================================ */
const DIRECT_FALLBACK_SOCIALS = [
  { title: "Instagram", body: "https://instagram.com/sundaydigital" },
  { title: "LinkedIn", body: "https://linkedin.com/company/sundaydigital" },
  { title: "Dribbble", body: "https://dribbble.com/sundaydigital" },
];
const DIRECT_FALLBACK_OFFICES = [
  { title: "Brooklyn HQ", body: "123 Front Street", addr2: "Brooklyn, NY 11201", note: "By appointment" },
  { title: "Remote · EU", body: "Distributed team", addr2: "Paris · Lisbon · Berlin", note: "9am–6pm CET" },
  { title: "Hours", body: "Mon — Fri", addr2: "9am — 6pm EST", note: "Reply ≤ 24h" },
];

const DirectDetails = () => {
  const cms = useContent("contact");
  const d = cms?.direct_details || {};
  // CMS toggle — admin can hide this whole section on /contact without
  // removing any data. Default = visible (existing pages unaffected).
  if (d.visible === false) return null;
  const eyebrow = d.eyebrow || "Direct line";
  const headlineMain = d.headline_main || "Old-fashioned";
  const headlineItalic = d.headline_italic || "still";
  const headlineAccent = d.headline_accent || "works";
  const email = d.email || "hello@sundaydigital.io";
  const socials = Array.isArray(d.socials) && d.socials.length ? d.socials : DIRECT_FALLBACK_SOCIALS;
  const offices = Array.isArray(d.offices) && d.offices.length ? d.offices : DIRECT_FALLBACK_OFFICES;

  return (
    <section
      data-testid="contact-details"
      className="relative bg-[#0F141A] border-t border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-12">
          <div className="lg:col-span-5">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
            <h2
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="mt-7 font-display text-[2rem] md:text-[2.75rem] lg:text-[3.25rem] font-black tracking-[-0.035em] leading-[1.05] text-white"
            >
              <span>
                {headlineMain}{" "}
                {headlineItalic ? (
                  <>
                    <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                  </>
                ) : null}
                <PillarAccent>{headlineAccent}</PillarAccent>.
              </span>
            </h2>
            <a
              href={`mailto:${email}`}
              className="group mt-9 inline-flex items-center gap-3 text-[1.1rem] md:text-[1.3rem] font-bold tracking-[-0.005em] text-white hover:text-[#F4D64E] transition-colors"
              data-testid="contact-email"
            >
              <Mail className="w-5 h-5 text-[#F4D64E]" />
              {email}
            </a>
            <div className="mt-10 flex items-center gap-6 flex-wrap">
              {socials.map((s, i) => (
                <a
                  key={i}
                  href={s.body}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-neutral-400 hover:text-[#F4D64E] transition-colors"
                >
                  {s.title}
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              ))}
            </div>
          </div>

          <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8">
            {offices.map((o, i) => (
              <li
                key={i}
                className="bg-[#0B0F14] border border-white/[0.06] rounded-2xl p-7 hover:border-[#F4D64E]/20 transition-colors"
              >
                <div className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] mb-5">
                  {o.title}
                </div>
                <div className="font-display text-[1.05rem] md:text-[1.15rem] font-bold tracking-[-0.005em] text-white leading-[1.4]">
                  {o.body}
                </div>
                <div className="text-[0.95rem] text-neutral-400 mt-1 leading-[1.45]">
                  {o.addr2}
                </div>
                <div className="mt-5 pt-4 border-t border-white/[0.08] text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                  {o.note}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   06 · Closing CTA
============================================================ */
const Closing = ({ onContact }) => {
  const cms = useContent("contact");
  const c = cms?.closing || {};
  const eyebrow = c.eyebrow || "Or just go for it";
  const headlineMain = c.headline_main || "Got the brief?";
  const headlineItalic = c.headline_italic || "Send it.";
  const headlineAccent = c.headline_accent || "We'll read it today";
  const ctaLabel = c.cta_label || "Send a brief";

  return (
    <section
      data-testid="contact-closing"
      className="relative bg-[#0B0F14] border-t border-white/[0.06] overflow-hidden"
    >
      <span
        aria-hidden
        className="absolute -top-32 -right-24 w-[40rem] h-[40rem] rounded-full bg-[#F4D64E]/[0.08] blur-3xl"
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40">
        <div className="max-w-4xl">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-8 font-display text-[2.25rem] sm:text-[3rem] md:text-[4.25rem] lg:text-[5.5rem] font-black tracking-[-0.04em] leading-[0.96] text-white"
          >
            <span>
              {headlineMain}{" "}
              {headlineItalic ? (
                <>
                  <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                </>
              ) : null}
              <PillarAccent>{headlineAccent}</PillarAccent>.
            </span>
          </h2>
          <div
            data-reveal="eyebrow"
            style={{ "--i": 2 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              type="button"
              onClick={onContact}
              data-testid="contact-closing-cta"
              className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
            >
              {ctaLabel}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   PAGE
============================================================ */
export default function ContactPage() {
  const { open: openContact } = useContactPanel();
  const cms = useContent("contact");
  const showBooking = !!(cms?.book_a_call?.visible);

  useSeo({
    route: "/contact",
    fallback: {
      title: "Contact — Sunday Digital",
      description: "Send a brief, book a call, or just say hello.",
    },
  });
  useEffect(() => {
    document.title = "Contact — Sunday Digital";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    const root = document.querySelector('[data-variant="v5"]');
    if (!root) return;
    const els = root.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    if (typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div
      data-testid="contact-page"
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <Hero onPrimary={openContact} />
        <Routing onPrimary={openContact} />
        {showBooking ? <BookACall onPrimary={openContact} /> : null}
        <FAQ />
        <DirectDetails />
        <Closing onContact={openContact} />
      </main>
      <Footer />
    </div>
  );
}
